import React from "react";
import { Box, Typography } from "@mui/material";
import shareIcon from "../../../assets/Images/TempleDetail/mobile/share-2.svg";
import borderLine from "../../../assets/Images/TempleDetail/mobile/border-line.svg";
import borderDiamond from "../../../assets/Images/TempleDetail/mobile/border-diamond.svg";
import heroMandala from "../../../assets/Images/TempleDetail/mobile/hero-mandala.svg";
import tipMandir from "../../../assets/Images/TempleDetail/mobile/tip-mandir.svg";
import factLandmark from "../../../assets/Images/TempleDetail/mobile/fact-landmark.svg";
import factCalendar from "../../../assets/Images/TempleDetail/mobile/fact-calendar.svg";
import factUsers from "../../../assets/Images/TempleDetail/mobile/fact-users.svg";
import factLock from "../../../assets/Images/TempleDetail/mobile/fact-lock.svg";
import { T, FONT, HINDI_FONT, Rupee, Photo } from "../shared";

// Figma "Mobile revamp" 739:17226 — Hero inset: photo on top, maroon panel with the title, and the
// white fundraising card with the four temple facts.

const FACT_ICONS = { managed: factLandmark, age: factCalendar, devotees: factUsers, status: factLock };
const AVATAR_COLORS = ["#F04951", "#C9952B", "#3B82F6"];
const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "TH";

// Gold rope down each side of the photo: a line with 13 diamonds (positions from the frame, in %).
const DIAMONDS = Array.from({ length: 13 }, (_, i) => 3.27 + i * 6.545);
const Border = ({ side }) => (
  <Box aria-hidden="true" sx={{ position: "absolute", top: 0, bottom: "14.89%", [side]: "3.01%", width: "1.28%", pointerEvents: "none" }}>
    <Box component="img" src={borderLine} alt="" sx={{ position: "absolute", left: "50%", top: 0, height: "100%", width: "1.2px", transform: "translateX(-50%)" }} />
    {DIAMONDS.map((top) => (
      <Box key={top} component="img" src={borderDiamond} alt="" sx={{ position: "absolute", left: 0, top: `${top}%`, width: "100%", aspectRatio: "6.5 / 5.89" }} />
    ))}
  </Box>
);

const MobileHero = ({ temple, donorNames = [], onShare }) => {
  const pct = temple.pct;
  const facts = temple.facts;
  const rows = [facts.slice(0, 2), facts.slice(2, 4)].filter((r) => r.length);

  return (
    <Box sx={{ pt: "16px", px: "12px" }}>
      <Box component="section" aria-label={temple.name} sx={{ position: "relative", background: T.maroon, borderRadius: "22px", overflow: "hidden" }}>
        {/* Temple photograph */}
        <Box sx={{ position: "relative", height: "163px", overflow: "hidden" }}>
          <Photo src={temple.heroImage} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(42,10,12,0) 30%, #2A0A0C 100%)" }} />
          <Border side="left" />
          <Border side="right" />
          <Box
            component="button"
            type="button"
            onClick={onShare}
            sx={{
              position: "absolute",
              right: "27px",
              top: "11px",
              display: "flex",
              alignItems: "center",
              gap: "7px",
              px: "12px",
              py: "9px",
              borderRadius: "100px",
              background: "rgba(31,8,10,0.62)",
              border: "1px solid rgba(255,255,255,0.22)",
              cursor: "pointer",
              "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
            }}
          >
            <Box component="img" src={shareIcon} alt="" sx={{ width: "14px", height: "14px" }} />
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "normal", color: "#FFFFFF" }}>Share</Typography>
          </Box>
        </Box>

        {/* Campaign message */}
        <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start", px: "22px", pb: "12px", overflow: "hidden" }}>
          <Box component="img" src={heroMandala} alt="" aria-hidden="true" sx={{ position: "absolute", left: "-34.15%", top: "-25%", width: "54.66%", aspectRatio: "1 / 1", pointerEvents: "none" }} />
          {temple.tagline && (
            <Typography sx={{ position: "relative", fontFamily: HINDI_FONT, fontSize: "17px", lineHeight: "normal", color: T.goldLight }}>{temple.tagline}</Typography>
          )}
          <Typography component="h1" sx={{ position: "relative", fontFamily: FONT, fontWeight: 700, fontSize: "34px", lineHeight: 1.12, color: "#FFFFFF", wordBreak: "break-word" }}>
            {temple.name}
          </Typography>
          {temple.subtitle && (
            <Typography sx={{ position: "relative", fontFamily: FONT, fontWeight: 600, fontSize: "16px", lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}>{temple.subtitle}</Typography>
          )}
          {temple.chips.length > 0 && (
            <Box sx={{ position: "relative", display: "flex", gap: "8px", flexWrap: "wrap", pt: "6px" }}>
              {temple.chips.map((chip) => (
                <Box key={chip.label} sx={{ display: "flex", alignItems: "center", height: "27px", px: "10px", boxSizing: "border-box", borderRadius: "100px", background: "rgba(255,255,255,0.12)", border: "0.8px solid rgba(255,255,255,0.25)" }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "10px", lineHeight: "13px", color: "#FFFFFF", whiteSpace: "nowrap" }}>{chip.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Fundraising card */}
        {(temple.showProgress || facts.length > 0) && (
          <Box sx={{ px: "12px", pb: "10px" }}>
            <Box sx={{ position: "relative", background: "#FFFFFF", border: "1px solid #EFE4D3", borderRadius: "16px", px: "16px", py: "12px", display: "flex", flexDirection: "column", gap: "11px" }}>
              {temple.showProgress && (
                <>
                  {(temple.showRaised || temple.showGoal) && (
                  <Box sx={{ display: "flex", gap: "15px", alignItems: "center" }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                      {temple.showRaised && (
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "24px", lineHeight: "32px", color: T.ink }}>
                        <Rupee value={temple.raised} />
                      </Typography>
                      )}
                      {temple.showGoal && (
                        <Typography sx={{ fontFamily: FONT, fontSize: temple.showRaised ? "12px" : "16px", fontWeight: temple.showRaised ? 400 : 700, lineHeight: 1.5, color: temple.showRaised ? T.muted : T.ink }}>
                          {temple.showRaised ? "raised of " : "Goal "}<Rupee value={temple.goal} />
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end", justifyContent: "center", minHeight: temple.showRaised ? "66px" : 0, flexShrink: 0 }}>
                      {temple.showGoal && (
                        <Box sx={{ background: "#FDE8E9", borderRadius: "100px", px: "10px", py: "5px" }}>
                          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: "normal", color: T.red, whiteSpace: "nowrap" }}>{pct}% funded</Typography>
                        </Box>
                      )}
                      {temple.showRaised && (
                      <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        {donorNames.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {donorNames.slice(0, 3).map((name, i) => (
                              <Box key={i} sx={{ width: "26px", height: "26px", borderRadius: "13px", border: "2px solid #FFFFFF", background: AVATAR_COLORS[i], ml: i ? "-8px" : 0, zIndex: 3 - i, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: "12px", color: "#FFFFFF" }}>{initials(name)}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: "20px", color: T.ink, whiteSpace: "nowrap" }}>
                          {Number(temple.backers || 0).toLocaleString("en-IN")} donors
                        </Typography>
                      </Box>
                      )}
                    </Box>
                  </Box>
                  )}

                  {/* Progress with the mandir marker riding the fill */}
                  <Box sx={{ position: "relative", height: "10px", my: "0px" }}>
                    <Box sx={{ position: "absolute", inset: 0, borderRadius: "5px", background: "#F1ECE6" }} />
                    <Box sx={{ position: "absolute", left: 0, top: 0, height: "10px", width: `${pct}%`, borderRadius: "5px", background: T.red, transition: "width 0.6s ease" }} />
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: "absolute",
                        top: "-10px",
                        left: `clamp(0px, calc(${pct}% - 15px), calc(100% - 30px))`,
                        width: "30px",
                        height: "30px",
                        boxSizing: "border-box",
                        borderRadius: "15px",
                        border: "3px solid #FFFFFF",
                        background: T.red,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box component="img" src={tipMandir} alt="" sx={{ width: "14px", height: "14px" }} />
                    </Box>
                  </Box>
                </>
              )}

              {facts.length > 0 && (
                <>
                  {temple.showProgress && <Box sx={{ height: "1px", background: "#EFE4D3" }} />}
                  {rows.map((row, r) => (
                    <Box key={r} sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                      {row.map((fact) => (
                        <Box key={fact.key} sx={{ flex: "1 1 0", minWidth: 0, display: "flex", gap: "8px", alignItems: "center" }}>
                          <Box sx={{ width: "28px", height: "34px", borderRadius: "9px", background: "rgba(242,178,186,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Box component="img" src={FACT_ICONS[fact.key]} alt="" sx={{ width: "16px", height: "16px" }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: 1.5, color: T.muted }}>{fact.label}</Typography>
                            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: 1.5, color: T.ink, wordBreak: "break-word" }}>{fact.value}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MobileHero;
